import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormDateField from '../../../components/ui/form/FormDateField'
import FormNumberField from '../../../components/ui/form/FormNumberField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { splitMinutes, toMinutes } from '../duration'
import { flightRecordFormSchema, type FlightRecordFormValues } from './flightRecordFormSchema'
import type { FlightRecord, FlightRecordUpsert } from '../types'

interface Props {
  visible: boolean
  initial: FlightRecord | null
  submitting: boolean
  onSubmit: (payload: FlightRecordUpsert) => void
  onHide: () => void
}

const EMPTY: FlightRecordFormValues = { flightDate: '', hours: 0, minutes: 0, description: '' }

/**
 * Uçuş Kaydı ekle/düzenle formu.
 *
 * <p>Süre **iki alanda** girilir (saat · dakika) ama tek sayı olarak gönderilir — veritabanı tek bir
 * `NUMBER` tutuyor ve birimi dakikadır. Çevrim `duration.ts`'te tek yerdedir; toplamın sıfırdan büyük
 * olması kuralı şemada, iki alanın **toplamı** üzerinde uygulanır.
 */
export default function FlightRecordFormDialog({
  visible,
  initial,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useZodForm(flightRecordFormSchema, {
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!visible) return
    if (!initial) {
      reset(EMPTY)
      return
    }
    const { hours, minutes } = splitMinutes(initial.durationMinutes)
    reset({
      flightDate: initial.flightDate,
      hours,
      minutes,
      description: initial.description ?? '',
    })
  }, [visible, initial, reset])

  // Sema null'i "zorunlu" hatasiyla reddeder; buraya yalnizca dolu deger ulasir.
  const onValid = (v: FlightRecordFormValues) =>
    onSubmit({
      flightDate: v.flightDate,
      durationMinutes: toMinutes(v.hours ?? 0, v.minutes ?? 0),
      description: v.description ? v.description : null,
    })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('timePortfolio.flight.form.edit') : t('timePortfolio.flight.form.new')}
      submitLabel={t('timePortfolio.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormDateField
          control={control}
          name="flightDate"
          label={t('timePortfolio.flight.columns.date')}
          required
        />
        {/* Sure TEK bir alandir, iki kutuya bolunmustur: ustte ne oldugunu soyleyen bir baslik
            ("Ucus Suresi"), altinda yan yana Saat ve Dakika. Kutularin kendi etiketleri de duruyor -
            hangisinin saat hangisinin dakika oldugu yer tutucuya birakilmaz. */}
        <div className="ui-field">
          <span className="ui-field-label">
            {t('timePortfolio.flight.columns.duration')} <span className="ui-req">*</span>
          </span>
          <div className="tp-time-row">
            <FormNumberField
              control={control}
              name="hours"
              label={t('timePortfolio.duration.hours')}
              min={0}
            />
            <FormNumberField
              control={control}
              name="minutes"
              label={t('timePortfolio.duration.minutes')}
              min={0}
            />
          </div>
        </div>
        <FormTextareaField
          control={control}
          name="description"
          label={t('timePortfolio.flight.columns.description')}
          full
        />
      </div>
    </FormDialog>
  )
}
